import * as React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { CssVarsProvider as JoyCssVarsProvider, extendTheme as extendJoyTheme, useColorScheme as useJoyColorScheme } from "@mui/joy/styles";
import GlobalStyles from "@mui/joy/GlobalStyles";
import CircularProgress from "@mui/joy/CircularProgress";
import Box from "@mui/joy/Box";
//import CssBaseline from "@mui/joy/CssBaseline";
import { StyledEngineProvider } from '@mui/joy/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { I18nextProvider } from 'react-i18next';

import {
  extendTheme as extendMaterialTheme,
  CssVarsProvider as MaterialCssVarsProvider,
  THEME_ID as MATERIAL_THEME_ID,
  useColorScheme as useMaterialColorScheme,
} from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import i18n from './i18n/config';
import { getUserSettings } from './services/settingsService';

import ErrorBoundary from './components/common/ErrorBoundary';
import {
  startPerformanceMonitoring,
  onMetric,
  logMetricsToConsole,
} from './utils/performanceMonitoring';
import {
  setupGlobalErrorHandlers,
  monitorNetworkErrors,
  onError,
  logErrorToConsole,
} from './hooks/useErrorMonitoring';

const materialTheme = extendMaterialTheme();
const joyTheme = extendJoyTheme();

const PreferenceInitializer = () => {
  const { setMode: setMaterialMode } = useMaterialColorScheme();
  const { setMode: setJoyMode } = useJoyColorScheme();

  React.useEffect(() => {
    let active = true;

    const loadPreferences = async () => {
      const settings = await getUserSettings();
      if (!settings || !active) {
        return;
      }

      if (settings.theme) {
        setMaterialMode(settings.theme);
        setJoyMode(settings.theme);
      }

      if (settings.language) {
        i18n.changeLanguage(settings.language);
        localStorage.setItem('i18nextLng', settings.language);
      }
    };

    loadPreferences();

    return () => {
      active = false;
    };
  }, [setMaterialMode, setJoyMode]);

  return null;
};

// Eager-loaded components (critical for initial render)
import MainLayout from './layouts/MainLayout';
import RequireProfileComplete from "./components/common/RequireProfileComplete";
import GlobalSnackbar from "./components/common/GlobalSnackbar";

// Lazy-loaded page components (loaded on demand)
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const Donation = React.lazy(() => import("./pages/Donation"));
const Profile = React.lazy(() => import("./pages/Profile"));
const CompleteProfile = React.lazy(() => import("./pages/CompleteProfile"));
const Donate = React.lazy(() => import("./pages/AddDonation"));
const DonationDetails = React.lazy(() => import("./pages/DonationDetails"));
const Fundraising = React.lazy(() => import("./pages/Fundraising"));
const Leaderboard = React.lazy(() => import("./pages/Leaderboard"));
const Support = React.lazy(() => import("./pages/Support"));
const NityaSeva = React.lazy(() => import("./pages/NityaSeva"));
const DarshanGallery = React.lazy(() => import("./pages/DarshanGallery"));
const DarshanGalleryDay = React.lazy(() => import("./pages/DarshanGalleryDay"));
const Error403 = React.lazy(() => import("./pages/Error403"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const EventPage = React.lazy(() => import("./pages/EventPage"));
const Events = React.lazy(() => import("./pages/Events"));
const Articles = React.lazy(() => import("./pages/Articles"));
const ArticleDetail = React.lazy(() => import("./pages/ArticleDetail"));
const IskconCalendarPage = React.lazy(() => import('./pages/IskconCalendar'));

// Lazy-loaded congregation components
const CongregationLayout = React.lazy(() => import('./pages/congregation/CongregationLayout'));
const CongregationEntryRedirect = React.lazy(() => import('./pages/congregation/CongregationEntryRedirect'));
const CongregationApplyPage = React.lazy(() => import('./pages/congregation/CongregationApplyPageClean'));
const CongregationPublicApplyPage = React.lazy(() => import('./pages/congregation/CongregationPublicApplyPage'));
const CongregationDevoteeRoute = React.lazy(() => import('./pages/congregation/CongregationDevoteeRoute'));
const CongregationMentorRoute = React.lazy(() => import('./pages/congregation/CongregationMentorRoute'));
const CongregationAdminRoute = React.lazy(() => import('./pages/congregation/CongregationAdminRoute'));
const AdminDevotesPanel = React.lazy(() => import('./pages/congregation/admin/AdminDevotesPanel'));
const AdminImportMembers = React.lazy(() => import('./pages/congregation/admin/AdminImportMembers'));
const MentorWorkspaceLayout = React.lazy(() => import('./pages/congregation/mentor/MentorWorkspaceLayout'));
const MentorWorkspaceSectionRouter = React.lazy(() => import('./pages/congregation/mentor/MentorWorkspaceSectionRouter'));
const DevoteeWorkspaceLanding = React.lazy(() => import('./pages/congregation/devotee/DevoteeWorkspaceLanding'));
const DevoteeWorkspaceLayout = React.lazy(() => import('./pages/congregation/devotee/DevoteeWorkspaceLayout'));
const DevoteeWorkspaceSectionRouter = React.lazy(() => import('./pages/congregation/devotee/DevoteeWorkspaceSectionRouter'));
const DevoteeSadhanaRoute = React.lazy(() => import('./pages/congregation/devotee/sadhana/DevoteeSadhanaRoute'));
const DevoteeDailyEntryLogPage = React.lazy(() => import('./pages/congregation/devotee/sadhana/DevoteeDailyEntryLogPage'));
// Congregation admin pages (ported from admin portal)
const CongregationDashboardPage = React.lazy(() => import('./pages/congregation/admin/CongregationDashboardPage'));
const CongregationApplicationsPage = React.lazy(() => import('./pages/congregation/admin/CongregationApplicationsPage'));
const CongregationMentorStaffingPage = React.lazy(() => import('./pages/congregation/admin/CongregationMentorStaffingPage'));
const CongregationMastersPage = React.lazy(() => import('./pages/congregation/admin/CongregationMastersPage'));
const MentorBatchesPage = React.lazy(() => import('./pages/congregation/admin/MentorBatchesPage'));
const AdminBooksPage = React.lazy(() => import('./pages/congregation/admin/AdminBooksPage'));
const AdminDevoteesPage = React.lazy(() => import('./pages/congregation/admin/AdminDevoteesPage'));
const AdminSadhanaPage = React.lazy(() => import('./pages/congregation/admin/AdminSadhanaPage'));
const CongregationAttendancePage = React.lazy(() => import('./pages/congregation/admin/CongregationAttendancePage'));
const CongregationServicesPage = React.lazy(() => import('./pages/congregation/admin/CongregationServicesPage'));
const CongregationServiceTypesPage = React.lazy(() => import('./pages/congregation/admin/CongregationServiceTypesPage'));

// New sadhana system pages (no templates required)
const CurrentWeekPage = React.lazy(() => import('./pages/sadhana/CurrentWeek'));
const HistoryPage = React.lazy(() => import('./pages/sadhana/History'));
const StatisticsPage = React.lazy(() => import('./pages/sadhana/Statistics'));
const MentorDashboardPage = React.lazy(() => import('./pages/sadhana/MentorDashboard'));

import { CongregationProvider } from './context/CongregationContext';
import { DevoteeSadhanaProvider } from './context/DevoteeSadhanaContext';

// Loading fallback component
const LoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '50vh',
    }}
  >
    <CircularProgress size="md" />
  </Box>
);



const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// add router here serve signin page at "/signin" route with all necessary props

/**
 * Initialize monitoring on app start
 */
function initializeMonitoring() {
  // Setup error monitoring
  setupGlobalErrorHandlers();
  monitorNetworkErrors();

  // Setup performance monitoring
  startPerformanceMonitoring();

  // Register handlers for metrics (in development, log to console)
  if (process.env.NODE_ENV === 'development') {
    onMetric((metric) => {
      logMetricsToConsole(metric);
    });

    onError((errorEvent) => {
      logErrorToConsole(errorEvent);
    });
  }

  // In production, you can send metrics to your monitoring service:
  // onMetric((metric) => {
  //   sendMetricsToService(metric, 'https://your-api.com/metrics');
  // });
  //
  // onError((errorEvent) => {
  //   sendErrorToService(errorEvent, 'https://your-api.com/errors');
  // });
}

// Initialize monitoring when app loads
initializeMonitoring();

// Force dark theme by clearing any stored theme preferences
// This ensures the app always starts in dark mode regardless of previous user preference
if (typeof window !== 'undefined' && window.localStorage) {
  // Clear MUI Joy theme storage (removes 'light', 'dark', or 'system' preference)
  window.localStorage.removeItem('mui-mode');
  // Clear Material UI theme storage
  window.localStorage.removeItem('mui-material-mode');
  
  // Also clear any potential custom storage keys
  const storageKeys = Object.keys(window.localStorage);
  storageKeys.forEach(key => {
    if (key.includes('mui') && key.includes('mode')) {
      window.localStorage.removeItem(key);
    }
  });
}

export default function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <StyledEngineProvider injectFirst>
        <QueryClientProvider client={queryClient}>
          <HelmetProvider>
            <ErrorBoundary>
              <Router>
                {/* <CssVarsProvider theme={customTheme} disableTransitionOnChange> */}
                <MaterialCssVarsProvider 
                  theme={{ [MATERIAL_THEME_ID]: materialTheme }}
                  defaultMode="dark"
                >
                  <JoyCssVarsProvider theme={joyTheme} defaultMode="dark">
                    <PreferenceInitializer />
                    <CssBaseline enableColorScheme />
                    <GlobalStyles
                      styles={{
                        body: {
                          fontFamily: "Roboto, sans-serif",
                          backgroundColor: "var(--joy-palette-background-body)",
                          color: "var(--joy-palette-text-primary)",
                        },
                      }}
                    />
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Routes>
                      <Route path="/congregation/apply-public" element={<CongregationPublicApplyPage />} />
                      <Route path="/profile/complete" element={<CompleteProfile />} />
                      <Route element={<RequireProfileComplete />}>
                        <Route element={<MainLayout />}>
                          <Route path="/" element={<Dashboard />} />
                          <Route path="/contribution" element={<Donation />} />
                          <Route path="/donate" element={<Donate />} />
                          <Route path="/donation/details" element={<DonationDetails />} />
                          <Route path="/nitya-seva" element={<NityaSeva />} />
                          <Route path="/fundraising" element={<Fundraising />} />
                          <Route path="/leaderboard" element={<Leaderboard />} />
                          <Route path="/daily-darshan" element={<DarshanGallery />} />
                          <Route path="/daily-darshan/:type" element={<DarshanGallery />} />
                          <Route path="/daily-darshan/:type/:date" element={<DarshanGalleryDay />} />
                          <Route path="/articles" element={<Articles />} />
                          <Route path="/articles/:id" element={<ArticleDetail />} />
                          <Route path="/events" element={<Events />} />
                          <Route path="/calendar" element={<IskconCalendarPage />} />
                          <Route path="/support" element={<Support />} />
                          <Route path="/profile" element={<Profile />} />
                          <Route path="/events" element={<Events />} />
                          <Route path="/event/:id" element={<EventPage />} />

                          {/* New Sadhana System (no templates) */}
                          <Route path="/congregation/devotee/sadhana" element={<CurrentWeekPage />} />
                          <Route path="/sadhana/history" element={<HistoryPage />} />
                          <Route path="/sadhana/statistics" element={<StatisticsPage />} />
                          <Route path="/congregation/mentor/sadhana" element={<MentorDashboardPage />} />

                          <Route
                            path="/congregation/*"
                            element={(
                              <CongregationProvider>
                                <CongregationLayout />
                              </CongregationProvider>
                            )}
                          >
                            <Route index element={<CongregationEntryRedirect />} />
                            <Route path="apply" element={<CongregationApplyPage />} />
                            <Route path="devotee/*" element={<CongregationDevoteeRoute />}>
                              <Route index element={<DevoteeWorkspaceLanding />} />
                              <Route element={<DevoteeWorkspaceLayout />}>
                                <Route path=":section/*" element={<DevoteeWorkspaceSectionRouter />} />
                              </Route>
                              <Route path="sadhana" element={<DevoteeSadhanaRoute />} />
                              <Route
                                path="sadhana/log"
                                element={(
                                  <DevoteeSadhanaProvider>
                                    <DevoteeDailyEntryLogPage />
                                  </DevoteeSadhanaProvider>
                                )}
                              />
                            </Route>
                            <Route path="mentor/*" element={<CongregationMentorRoute />}>
                              <Route index element={<Navigate to="/congregation/mentor/overview" replace />} />
                              <Route element={<MentorWorkspaceLayout />}>
                                <Route path=":section/*" element={<MentorWorkspaceSectionRouter />} />
                              </Route>
                            </Route>
                            <Route path="admin/*" element={<CongregationAdminRoute />}>
                              <Route index element={<Navigate to="/congregation/admin/devotees" replace />} />
                              <Route path="devotees" element={<AdminDevotesPanel />} />
                              <Route path="import" element={<AdminImportMembers />} />
                              <Route path="dashboard" element={<CongregationDashboardPage />} />
                              <Route path="applications" element={<CongregationApplicationsPage />} />
                              <Route path="mentor-staffing" element={<CongregationMentorStaffingPage />} />
                              <Route path="masters" element={<CongregationMastersPage />} />
                              <Route path="batches" element={<MentorBatchesPage />} />
                              <Route path="books" element={<AdminBooksPage />} />
                              <Route path="all-devotees" element={<AdminDevoteesPage />} />
                              <Route path="sadhana" element={<AdminSadhanaPage />} />
                              <Route path="attendance" element={<CongregationAttendancePage />} />
                              <Route path="services" element={<CongregationServicesPage />} />
                              <Route path="service-types" element={<CongregationServiceTypesPage />} />
                            </Route>
                          </Route>
                        </Route>
                        <Route path="/unauthorised" element={<Error403 />} />
                      </Route>
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </React.Suspense>
                  <GlobalSnackbar />
                </JoyCssVarsProvider>
              </MaterialCssVarsProvider>
              {/* </CssVarsProvider> */}
            </Router>
          </ErrorBoundary>
        </HelmetProvider>
      </QueryClientProvider>
    </StyledEngineProvider>
    </I18nextProvider>
  );
}
